import React, { useEffect, useState } from 'react';
import { authenticatedFetch } from '../libraries/auth';
import { getFriendlyErrorMessage } from '../libraries/friendlyErrors';
import { Participant, ParticipantSettings } from '../types';
import FloatingMessageBox from './FloatingMessageBox';
import { unregisterNotifications } from '@/libraries/notification';

interface SettingsPageProps {
    settings?: ParticipantSettings | null;
    onSettingsSaved: (settings: ParticipantSettings) => void;
    blockedParticipants: Participant[];
    onParticipantUnblocked: (participantId: number, userId: number) => void;
}

const API_URL = import.meta.env.VITE_API_URL || '';

const SettingsPage: React.FC<SettingsPageProps> = ({ settings, onSettingsSaved, blockedParticipants, onParticipantUnblocked }) => {
    const [pushNotifications, setPushNotifications] = useState<boolean>(settings?.pushNotifications ?? true);
    const [saving, setSaving] = useState(false);
    const [sendingRemovalRequest, setSendingRemovalRequest] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    useEffect(() => {
        setPushNotifications(settings?.pushNotifications ?? true);
    }, [settings?.pushNotifications]);

    const handleSave = async () => {
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);

            const response = await authenticatedFetch(`${API_URL}/api/participant/settings`, {
                method: 'PUT',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({pushNotifications}),
            });

            const data = await response.json().catch(() => null);
            if (!response.ok) {
                throw new Error(data?.error || 'Einstellungen konnten nicht gespeichert werden');
            }

            const updatedSettings: ParticipantSettings = {
                pushNotifications: !!data.pushNotifications,
            };
            onSettingsSaved(updatedSettings);
            if (!updatedSettings.pushNotifications) {
                await unregisterNotifications()
            }
            setSuccess('Einstellungen gespeichert');
        } catch (e) {
            console.error('Failed to update settings:', e);
            setError(getFriendlyErrorMessage(e, {
                genericMessage: 'Ihre Einstellungen konnten gerade nicht gespeichert werden. Bitte versuchen Sie es erneut.',
                participantMessage: 'Ihr Teilnehmerprofil konnte nicht geladen werden. Bitte versuchen Sie es später erneut.',
            }));
        } finally {
            setSaving(false);
        }
    };

    const handleUnblockParticipant = async (participant: Participant) => {
        try {
            setSaving(true);
            setError(null);
            setSuccess(null);

            const response = await authenticatedFetch(`${API_URL}/api/participant/block`, {
                method: 'DELETE',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({blockedParticipantId: participant.id}),
            });

            const data = await response.json().catch(() => null);
            if (!response.ok) {
                throw new Error(data?.error || 'Teilnehmer konnte nicht entsperrt werden');
            }

            onParticipantUnblocked(participant.id, participant.userId);
            setSuccess(`${participant.vorname} ${participant.nachname} wurde entsperrt.`);
        } catch (e) {
            console.error('Failed to unblock participant:', e);
            setError(getFriendlyErrorMessage(e, {
                genericMessage: 'Der Teilnehmer konnte gerade nicht entsperrt werden. Bitte versuchen Sie es erneut.',
                participantMessage: 'Der Teilnehmer konnte gerade nicht entsperrt werden. Bitte versuchen Sie es später erneut.',
            }));
        } finally {
            setSaving(false);
        }
    };

    const handleAccountRemovalRequest = async () => {
        try {
            setSendingRemovalRequest(true);
            setError(null);
            setSuccess(null);

            const response = await authenticatedFetch(`${API_URL}/api/participant/account-removal-request`, {
                method: 'POST',
            });

            const data = await response.json().catch(() => null);
            if (!response.ok) {
                throw new Error(data?.error || 'Anfrage zur Kontoloeschung konnte nicht gesendet werden');
            }

            setSuccess('Ihr Konto wird innerhalb von 48 Stunden gelöscht.');
        } catch (e) {
            console.error('Failed to send account removal request:', e);
            setError(getFriendlyErrorMessage(e, {
                genericMessage: 'Die Anfrage zur Kontolöschung konnte gerade nicht gesendet werden. Bitte versuchen Sie es erneut.',
                participantMessage: 'Ihr Teilnehmerprofil konnte nicht geladen werden. Bitte versuchen Sie es später erneut.',
            }));
        } finally {
            setSendingRemovalRequest(false);
        }
    };

    return (
        <div className="space-y-4 p-4 bg-gray-50 rounded-lg shadow-2xl border-1 border-gray-200">
            {error && (
                <FloatingMessageBox
                    title="Fehler"
                    message={error}
                    type="error"
                    onClose={() => setError(null)}
                />
            )}
            {success && (
                <FloatingMessageBox
                    title="Erfolg"
                    message={success}
                    type="success"
                    autoHideMs={2500}
                    onClose={() => setSuccess(null)}
                />
            )}

            {__IS_CORDOVA__ && (
                <div className="bg-white rounded-lg shadow-sm p-6">
                    <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-4">Einstellungen</h2>

                    <label className="flex items-center justify-between gap-4 border border-gray-200 rounded-lg p-4">
                        <div>
                            <p className="text-sm md:text-base font-medium text-gray-800">Push-Benachrichtigungen</p>
                            <p className="text-xs md:text-sm text-gray-500">Benachrichtigungen für wichtige Updates erhalten.</p>
                        </div>
                        <input
                            type="checkbox"
                            checked={pushNotifications}
                            onChange={(e) => setPushNotifications(e.target.checked)}
                            className="h-5 w-5 rounded border-gray-300 text-pink-600 focus:ring-pink-500"
                        />
                    </label>

                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={saving}
                        className="mt-4 w-full md:w-auto md:px-8 bg-pink-600 text-white py-3 rounded-lg font-medium hover:bg-pink-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {saving ? 'Speichern...' : 'Speichern'}
                    </button>
                </div>
            )}

            <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-4">Blockierte Teilnehmer</h2>

                {blockedParticipants.length === 0 ? (
                    <p className="text-sm text-gray-500">Sie haben aktuell keine Teilnehmer blockiert.</p>
                ) : (
                    <ul className="space-y-3">
                        {blockedParticipants.map((participant) => (
                            <li key={participant.id} className="flex items-center justify-between gap-3 border border-gray-200 rounded-lg p-3">
                                <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-800 truncate">
                                        {participant.vorname} {participant.nachname}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">{participant.standort}</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleUnblockParticipant(participant)}
                                    disabled={saving}
                                    className="shrink-0 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                                >
                                    Entsperren
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            <div className="bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg md:text-xl font-semibold text-gray-800 mb-2">Konto löschen</h2>
                <p className="text-sm text-gray-500 mb-4">
                    Sie können die Löschung Ihres Kontos anfordern.
                </p>
                <button
                    type="button"
                    onClick={handleAccountRemovalRequest}
                    disabled={sendingRemovalRequest}
                    className="w-full md:w-auto md:px-8 bg-red-600 text-white py-3 rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                >
                    {sendingRemovalRequest ? 'Wird gesendet...' : 'Kontolöschung anfordern'}
                </button>
            </div>
        </div>
    );
};

export default SettingsPage;
